import React, { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RecoilRoot } from 'recoil';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { ImThemeProvider } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import LocalizationProviderConnector from './LocalizationProviderConnector';
import UserRoleProvider from './UserRoleProvider';

import { BuildConfigProvider } from '../config';
import { AuthProvider } from '../auth';
import DatadogRumProvider from './DatadogRumProvider';
import { FeatureFlagProvider } from './FeatureFlagProvider';

const Router = process.env.NODE_ENV === 'test' ? MemoryRouter : BrowserRouter;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // for dev
      retry: 1, // default is 3
      staleTime: 5000,
    },
  },
});

type Props = {
  basename?: string;
  children: ReactNode;
  envVars: any;
};

export function AppProviders(props: Readonly<Props>) {
  const { basename, children, envVars } = props;

  return (
    <React.StrictMode>
      <RecoilRoot>
        <BuildConfigProvider envVars={envVars}>
          <LocalizationProviderConnector>
            <ImThemeProvider>
              <MuiProviders>
                <QueryClientProvider client={queryClient}>
                  <Router basename={basename}>
                    <AuthProvider>
                      <DatadogRumProvider>
                        <FeatureFlagProvider>
                          <UserRoleProvider>{children}</UserRoleProvider>
                        </FeatureFlagProvider>
                      </DatadogRumProvider>
                    </AuthProvider>
                  </Router>
                </QueryClientProvider>
              </MuiProviders>
            </ImThemeProvider>
          </LocalizationProviderConnector>
        </BuildConfigProvider>
      </RecoilRoot>
    </React.StrictMode>
  );
}
